import { useEffect, useRef, useState } from "react";
import {
  HiArrowUturnLeft,
  HiCheckCircle,
  HiEllipsisVertical,
  HiMoon,
  HiNoSymbol,
} from "react-icons/hi2";
import { REJECT_REASONS } from "./signalLifecycle";
import { ResolveSignalModal } from "./Modals/ResolveSignalModal";
import { SnoozeSignalModal } from "./Modals/SnoozeSignalModal";

/** Close a popover when clicking outside of it. */
const usePopoverClose = (open, onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);
  return ref;
};

/** Dropdown of reject reasons (+ optional note for "Other"). */
const RejectMenu = ({ onReject, onClose, align = "right" }) => {
  const ref = usePopoverClose(true, onClose);
  const [otherNote, setOtherNote] = useState(null); // null = not picking "other"

  if (otherNote !== null) {
    return (
      <div
        ref={ref}
        data-testid="reject-note-panel"
        className={`absolute ${align === "right" ? "right-0" : "left-0"} z-50 mt-1 w-60 space-y-2 rounded-lg border border-gray-200 bg-white p-3 shadow-lg`}
      >
        <p className="text-[10px] font-semibold text-gray-600">
          Why is this signal wrong? (optional)
        </p>
        <textarea
          value={otherNote}
          onChange={(e) => setOtherNote(e.target.value)}
          placeholder="What made this signal incorrect?"
          rows={2}
          autoFocus
          data-testid="reject-note-textarea"
          className="w-full resize-none rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="flex gap-2">
          <button
            onClick={() => onReject("other", otherNote)}
            className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
          >
            Reject signal
          </button>
          <button
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid="reject-menu"
      className={`absolute ${align === "right" ? "right-0" : "left-0"} z-50 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg`}
    >
      <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        Reject — reason
      </p>
      {REJECT_REASONS.map(({ value, label }) => (
        <button
          key={value}
          onClick={() =>
            value === "other" ? setOtherNote("") : onReject(value)
          }
          className="flex w-full items-center px-3 py-1.5 text-xs text-gray-700 transition-colors hover:bg-gray-50"
        >
          {label}
        </button>
      ))}
    </div>
  );
};

/** Kebab menu for collapsed rows — holds the low-frequency Reject action. */
const KebabMenu = ({ onReject, loading }) => {
  const [open, setOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const ref = usePopoverClose(open && !rejectOpen, () => setOpen(false));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="More actions"
        data-testid="signal-kebab"
        disabled={loading}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex size-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
      >
        <HiEllipsisVertical className="size-4" />
      </button>
      {open && !rejectOpen && (
        <div className="absolute right-0 z-50 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          <button
            onClick={() => setRejectOpen(true)}
            className="flex w-full items-center gap-2 px-3 py-2 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
          >
            <HiNoSymbol className="size-3.5" />
            Reject signal
          </button>
        </div>
      )}
      {rejectOpen && (
        <RejectMenu
          onReject={(reason, note) => {
            setRejectOpen(false);
            setOpen(false);
            onReject(reason, note);
          }}
          onClose={() => {
            setRejectOpen(false);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
};

/**
 * Lifecycle action controls for a signal.
 *
 * @param {string}   status          - active | resolved | snoozed | rejected
 * @param {"inline"|"bar"} variant   - "inline" for collapsed rows, "bar" for the expanded footer
 * @param {number}   [openActionCount] - Open (not done) actions; gates the resolve confirm
 * @param {Function} onResolve       - () => Promise
 * @param {Function} onSnooze        - (snoozeUntilISO) => Promise
 * @param {Function} onReject        - (reason, note?) => Promise
 * @param {Function} onReactivate    - () => Promise (revert a terminal signal to active)
 */
export const SignalLifecycleActions = ({
  status,
  variant = "inline",
  openActionCount = 0,
  onResolve,
  onSnooze,
  onReject,
  onReactivate,
}) => {
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [resolveModalOpen, setResolveModalOpen] = useState(false);
  const [snoozeModalOpen, setSnoozeModalOpen] = useState(false);

  const run =
    (fn) =>
    async (...args) => {
      if (loading) return;
      setLoading(true);
      setRejectOpen(false);
      setResolveModalOpen(false);
      setSnoozeModalOpen(false);
      try {
        await fn?.(...args);
      } finally {
        setLoading(false);
      }
    };

  const isBar = variant === "bar";

  // ---- Non-active signals: a Reactivate control reverts a mistaken action.
  // TODO(BE): resolved/rejected need un-resolve / un-dismiss endpoints before
  // their Reactivate can be surfaced. Today only snoozed is reversible
  // (unsnooze), so resolved/rejected render no action.
  const canReactivate = status === "snoozed";
  if (status !== "active") {
    if (!canReactivate) return null;
    return (
      <button
        type="button"
        disabled={loading}
        data-testid="signal-reactivate"
        onClick={run(onReactivate)}
        className={`inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 ${
          isBar ? "px-3.5 py-2 text-sm" : "h-7 px-2.5 text-[11px]"
        }`}
      >
        <HiArrowUturnLeft className={isBar ? "size-4" : "size-3"} />
        Reactivate
      </button>
    );
  }

  // ---- Active signals ----
  const handleResolveClick = () => {
    if (openActionCount > 0) setResolveModalOpen(true);
    else run(onResolve)();
  };

  const snooze = (
    <button
      type="button"
      disabled={loading}
      data-testid="signal-snooze"
      onClick={() => setSnoozeModalOpen(true)}
      className={`inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 ${
        isBar ? "px-3.5 py-2 text-sm" : "h-7 px-2.5 text-[11px]"
      }`}
    >
      <HiMoon className={isBar ? "size-4" : "size-3"} />
      Snooze
    </button>
  );

  const resolve = (
    <button
      type="button"
      disabled={loading}
      data-testid="signal-resolve"
      onClick={handleResolveClick}
      className={
        isBar
          ? "inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
          : "inline-flex h-7 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-[11px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50"
      }
    >
      <HiCheckCircle className={isBar ? "size-4" : "size-3"} />
      Resolve
    </button>
  );

  // Modals are portalled, so they can live anywhere in the active-state markup.
  const modals = (
    <>
      <ResolveSignalModal
        show={resolveModalOpen}
        openCount={openActionCount}
        loading={loading}
        onConfirm={run(onResolve)}
        onCancel={() => setResolveModalOpen(false)}
      />
      <SnoozeSignalModal
        show={snoozeModalOpen}
        loading={loading}
        onConfirm={run((until) => onSnooze(until))}
        onCancel={() => setSnoozeModalOpen(false)}
      />
    </>
  );

  if (isBar) {
    // Expanded card footer: Reject (destructive) · Snooze · Resolve (primary)
    return (
      <div className="flex items-center gap-2">
        <div className="relative">
          <button
            type="button"
            disabled={loading}
            data-testid="signal-reject"
            onClick={() => setRejectOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3.5 py-2 text-sm font-medium text-red-600 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            <HiNoSymbol className="size-4" />
            Reject
          </button>
          {rejectOpen && (
            <RejectMenu
              align="left"
              onReject={run((reason, note) => onReject(reason, note))}
              onClose={() => setRejectOpen(false)}
            />
          )}
        </div>
        {snooze}
        {resolve}
        {modals}
      </div>
    );
  }

  // Inline (collapsed row): Resolve · Snooze · kebab(Reject)
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {resolve}
      {snooze}
      <KebabMenu
        loading={loading}
        onReject={run((reason, note) => onReject(reason, note))}
      />
      {modals}
    </div>
  );
};
